import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Button, Card, Field, Select } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { canSeeLicenses } from '../licenses/data';
import { formatDate, formatMoney, type PersonDetail, type PersonLicense, today } from './data';

interface NoSeats {
  code: 'no_seats';
  message: string;
  cost: { monthly: number; upfront: number };
}

/**
 * Licencias de la persona: las vigentes (con liberar) y las anteriores, y asignar una nueva. Usa las rutas de
 * /api/licencias (guía 7.6): sin puestos libres pide confirmar la compra de uno más, con su costo. Un "cambio de
 * licencia" es liberar una y asignar otra.
 */
export function PersonLicenses({ person: p, onDone }: { person: PersonDetail; onDone: (message: string) => void }) {
  const me = useMe();
  const current = p.licenses.filter((l) => l.endsOn === null || l.endsOn >= today());
  const past = p.licenses.filter((l) => !current.includes(l));
  return (
    <Card title={t('us.ficha_licencias')}>
      {current.length === 0 ? (
        <p className="text-sm text-slate-500">{t('us.ficha_sin_licencias')}</p>
      ) : (
        <LicenseRows rows={current} linked={canSeeLicenses(me)} />
      )}
      {past.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-slate-600">
            {t('us.ficha_licencias_pasadas')} ({past.length})
          </summary>
          <div className="mt-2">
            <LicenseRows rows={past} linked={canSeeLicenses(me)} />
          </div>
        </details>
      )}
      {p.can.assignLicenses && p.assignableLicenses.length > 0 && <AssignForm person={p} onDone={onDone} />}
    </Card>
  );
}

function LicenseRows({ rows, linked }: { rows: PersonLicense[]; linked: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs text-slate-500">
          <tr>
            <th className="py-2 pr-4 font-medium">{t('us.campo_licencia')}</th>
            <th className="py-2 pr-4 font-medium">{t('li.col_desde')}</th>
            <th className="py-2 pr-4 font-medium">{t('li.col_hasta')}</th>
            <th className="py-2 font-medium">
              <span className="sr-only">{t('us.col_acciones')}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((l) => (
            <tr key={l.id} className="align-top">
              <td className="py-2 pr-4">
                {linked ? (
                  <Link to={`/licencias/${l.licenseTypeId}`} className="font-medium text-marca hover:underline">
                    {l.name}
                  </Link>
                ) : (
                  <span className="font-medium text-slate-900">{l.name}</span>
                )}
                <span className="block text-xs text-slate-500">
                  {t(l.billing === 'mensual' ? 'li.mensual' : 'li.anual')}
                  {l.source !== 'manual' && ` · ${t(`li.origen_${l.source}` as 'li.origen_alta')}`}
                  {l.note && ` · ${l.note}`}
                </span>
              </td>
              <td className="py-2 pr-4 text-slate-700">{formatDate(l.startsOn)}</td>
              <td className="py-2 pr-4 text-slate-700">{l.endsOn ? formatDate(l.endsOn) : '—'}</td>
              <td className="py-2">{l.canRelease && <ReleaseButton license={l} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Liberar: una mensual deja de costar; una anual vuelve al pool y sigue pagada hasta la renovación. */
function ReleaseButton({ license: l }: { license: PersonLicense }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [endsOn, setEndsOn] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open)
    return (
      <Button variant="link" className="text-sm" onClick={() => setOpen(true)}>
        {t('li.liberar')}
      </Button>
    );
  async function release() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/licencias/asignaciones/${l.id}/liberar`, { json: { endsOn } });
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      await qc.invalidateQueries({ queryKey: ['licencias'] });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <div className="w-52 space-y-2">
      {error && <Alert>{error}</Alert>}
      <Field id={`us-hasta-${l.id}`} type="date" label={t('li.ultimo_dia')} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
      <p className="text-xs text-slate-500">{l.billing === 'mensual' ? t('us.deja_mensual') : t('us.deja_anual')}</p>
      <div className="flex gap-2">
        <Button variant="danger" disabled={busy || !endsOn} onClick={() => void release()}>
          {t('li.liberar')}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>
          {t('comun.volver')}
        </Button>
      </div>
    </div>
  );
}

function AssignForm({ person: p, onDone }: { person: PersonDetail; onDone: (message: string) => void }) {
  const qc = useQueryClient();
  const [licenseId, setLicenseId] = useState('');
  const [startsOn, setStartsOn] = useState(today());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noSeats, setNoSeats] = useState<NoSeats | null>(null);

  async function assign(e?: FormEvent, confirmExtraSeat = false) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ extraSeat: boolean }>(`/api/licencias/${licenseId}/asignaciones`, { json: { userId: p.id, startsOn, note: note.trim() || null, confirmExtraSeat } });
      setNoSeats(null);
      setLicenseId('');
      setNote('');
      onDone(res.extraSeat ? t('li.asignada_extra_ok', { nombre: p.fullName }) : t('li.asignada_ok', { nombre: p.fullName }));
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      await qc.invalidateQueries({ queryKey: ['licencias'] });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as Partial<NoSeats> | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'no_seats') setNoSeats(body as NoSeats);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void assign(e)} className="mt-4 space-y-3 border-t border-slate-200 pt-4">
      <h3 className="text-sm font-semibold text-slate-900">{t('us.asignar_licencia')}</h3>
      {error && <Alert>{error}</Alert>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          id="us-licencia"
          label={t('us.campo_licencia')}
          value={licenseId}
          onChange={(e) => {
            setLicenseId(e.target.value);
            setNoSeats(null);
          }}
        >
          <option value="">{t('us.elegir_licencia')}</option>
          {p.assignableLicenses.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
        <Field id="us-licencia-desde" type="date" label={t('li.col_desde')} required value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
      </div>
      <Field id="us-licencia-nota" label={t('li.nota')} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      {noSeats ? (
        <Alert tone="warning">
          <p>{noSeats.message}</p>
          <p className="mt-1 font-medium">
            {noSeats.cost.monthly > 0 ? t('us.por_mes', { monto: formatMoney(noSeats.cost.monthly) }) : t('li.costo_puesto_extra', { monto: formatMoney(noSeats.cost.upfront) })}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void assign(undefined, true)}>
              {t('li.comprar_puesto')}
            </Button>
            <Button variant="secondary" onClick={() => setNoSeats(null)}>
              {t('comun.cancelar')}
            </Button>
          </div>
        </Alert>
      ) : (
        <Button type="submit" disabled={busy || !licenseId || !startsOn}>
          {t('us.asignar_licencia')}
        </Button>
      )}
    </form>
  );
}
